import { useRef, useCallback } from 'react';

/**
 * Hook that returns a playBeep function using Web Audio API.
 * No audio files needed — generates synthetic sine-wave tones.
 *
 * AudioContext is created lazily on first call (must happen
 * inside a user gesture on mobile browsers).
 */
export function useBeep() {
  const ctxRef = useRef<AudioContext | null>(null);

  const getContext = useCallback((): AudioContext | null => {
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      if (ctxRef.current.state === 'suspended') {
        ctxRef.current.resume();
      }
      return ctxRef.current;
    }
    try {
      ctxRef.current = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext)();
      return ctxRef.current;
    } catch {
      return null;
    }
  }, []);

  /**
   * Play a beep tone.
   * @param frequency Hz (e.g. 800 for tick, 1200 for final)
   * @param durationMs milliseconds (e.g. 100 for tick, 300 for final)
   * @param volume 0-1 (default 0.3)
   */
  const playBeep = useCallback(
    (frequency: number, durationMs: number, volume = 0.3) => {
      const ctx = getContext();
      if (!ctx) return;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = frequency;
      osc.type = 'sine';
      gain.gain.value = volume;

      // Fade out at the end to avoid click artifact
      const endTime = ctx.currentTime + durationMs / 1000;
      gain.gain.setValueAtTime(volume, endTime - 0.02);
      gain.gain.linearRampToValueAtTime(0, endTime);

      osc.start(ctx.currentTime);
      osc.stop(endTime);
    },
    [getContext]
  );

  /** Clean up AudioContext (call on unmount). */
  const dispose = useCallback(() => {
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      ctxRef.current.close();
      ctxRef.current = null;
    }
  }, []);

  return { playBeep, dispose };
}
